import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiPatch, type InboxItem, type InboxItemList } from "@/lib/api";

export interface UpdateInboxItemVars {
  id: string;
  item_type?: string;
  status?: "new" | "ack" | "archived" | "ignore";
  dsl_text?: string;
  ignore_reason?: string;
}

export interface UpdateInboxItemsVars {
  inbox_item_ids: string[];
  status: "new" | "ack" | "archived" | "ignore";
  ignore_reason?: string;
}

interface UpdateInboxItemsResponse {
  updated_count: number;
  items: InboxItem[];
}

async function patchInboxItem(vars: UpdateInboxItemVars): Promise<InboxItem> {
  const { id, ...body } = vars;
  return apiPatch<InboxItem>(`/api/v1/inbox/${id}`, body);
}

async function patchInboxItems(
  vars: UpdateInboxItemsVars,
): Promise<UpdateInboxItemsResponse> {
  return apiPatch<UpdateInboxItemsResponse>("/api/v1/inbox/bulk", vars);
}

/**
 * Optimistic update across cached inbox lists. Applies partial diff to every
 * matching row so the UI reflects the change immediately; rolls back if the
 * PATCH fails.
 */
export function useUpdateInboxItem() {
  const qc = useQueryClient();
  return useMutation<
    InboxItem,
    Error,
    UpdateInboxItemVars,
    { prev: Map<unknown, InboxItemList> }
  >({
    mutationFn: patchInboxItem,
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["inbox"] });
      const prev = new Map<unknown, InboxItemList>();
      qc.getQueriesData<InboxItemList>({ queryKey: ["inbox"] }).forEach(
        ([key, data]) => {
          if (!data) return;
          prev.set(key, data);
          qc.setQueryData<InboxItemList>(key, {
            ...data,
            items: data.items.map((a) =>
              a.id === vars.id
                ? {
                    ...a,
                    ...(vars.item_type != null
                      ? { item_type: vars.item_type }
                      : {}),
                    ...(vars.status != null ? { status: vars.status } : {}),
                    ...(vars.dsl_text != null ? { dsl_text: vars.dsl_text } : {}),
                  }
                : a,
            ),
          });
        },
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      ctx?.prev.forEach((data, key) =>
        qc.setQueryData(key as readonly unknown[], data),
      );
      toast.error(`Update failed: ${err.message}`);
    },
    onSettled: () => {
      // Re-fetch once server is done — filtered-by-status lists may
      // add/remove rows based on the new status, which optimistic patch
      // can't know about.
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["signal-candidates"] });
    },
  });
}

export function useUpdateInboxItems() {
  const qc = useQueryClient();
  return useMutation<UpdateInboxItemsResponse, Error, UpdateInboxItemsVars>({
    mutationFn: patchInboxItems,
    onError: (err) => {
      toast.error(`Update failed: ${err.message}`);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["signal-candidates"] });
    },
  });
}
